import React, { useState } from 'react';
import { 
  Phone, 
  PhoneCall, 
  PhoneIncoming, 
  PhoneOutgoing, 
  Search, 
  Filter,
  Calendar,
  Clock,
  User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

// Mock data for call history
const mockCallLogs = [
  {
    id: 'call_001',
    driverId: 'driver_001',
    driverName: 'Nguyễn Văn A',
    driverPhone: '0901234567',
    callType: 'outgoing',
    recipientPhone: '0987654321',
    recipientName: 'Khách hàng Trần B',
    duration: '00:02:45',
    timestamp: '2025-01-13T14:30:00+07:00',
    status: 'completed',
    orderId: 'DH001',
    purpose: 'Xác nhận địa chỉ giao hàng'
  },
  {
    id: 'call_002',
    driverId: 'driver_002',
    driverName: 'Trần Văn B',
    driverPhone: '0912345678',
    callType: 'incoming',
    recipientPhone: '0369852147',
    recipientName: 'Khách hàng Lê C',
    duration: '00:01:23',
    timestamp: '2025-01-13T13:15:00+07:00',
    status: 'completed',
    orderId: 'DH002',
    purpose: 'Hỗ trợ tìm địa chỉ'
  },
  {
    id: 'call_003',
    driverId: 'driver_001',
    driverName: 'Nguyễn Văn A',
    driverPhone: '0901234567',
    callType: 'outgoing',
    recipientPhone: '0147258369',
    recipientName: 'Khách hàng Phạm D',
    duration: '00:00:15',
    timestamp: '2025-01-13T12:45:00+07:00',
    status: 'missed',
    orderId: 'DH003',
    purpose: 'Thông báo giao hàng'
  }
];

const CallHistory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedCall, setSelectedCall] = useState(null);

  const filteredCalls = mockCallLogs.filter(call => {
    const matchesSearch = call.driverName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         call.recipientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         call.orderId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || call.callType === filterType;
    return matchesSearch && matchesType;
  });

  const getCallTypeIcon = (type) => {
    switch (type) {
      case 'incoming':
        return <PhoneIncoming className="h-4 w-4 text-green-600" />;
      case 'outgoing':
        return <PhoneOutgoing className="h-4 w-4 text-blue-600" />;
      default:
        return <Phone className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'missed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Lịch sử cuộc gọi</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Danh sách cuộc gọi */}
        <Card className="lg:col-span-2 animate-fade-in">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <PhoneCall className="h-5 w-5 mr-2 text-primary" />
                Danh sách cuộc gọi
              </CardTitle>
              <div className="flex space-x-2">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                  <Input
                    placeholder="Tìm kiếm..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-48"
                  />
                </div>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-2 border border-input rounded-md text-sm bg-background"
                >
                  <option value="all">Tất cả</option>
                  <option value="incoming">Cuộc gọi đến</option>
                  <option value="outgoing">Cuộc gọi đi</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredCalls.map((call) => (
                <div
                  key={call.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                    selectedCall?.id === call.id ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                  onClick={() => setSelectedCall(call)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="bg-primary/10 p-2 rounded-lg">
                        {getCallTypeIcon(call.callType)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{call.driverName}</h3>
                          <Badge variant="outline" className="text-xs">{call.orderId}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {new Date(call.timestamp).toLocaleString('vi-VN')}
                        </p>
                      </div>
                    </div>
                    <Badge className={getStatusColor(call.status)}>
                      {call.status === 'completed' ? 'Hoàn thành' : 'Nhỡ'}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>{call.recipientName}</span>
                      </div>
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>{call.recipientPhone}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>Thời lượng: {call.duration}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {call.purpose}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Chi tiết cuộc gọi */}
        <Card className="animate-fade-in">
          <CardHeader>
            <CardTitle>Chi tiết cuộc gọi</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedCall ? (
              <div className="space-y-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <h3 className="font-semibold mb-2">Thông tin tài xế</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Tên:</span> {selectedCall.driverName}</p>
                    <p><span className="font-medium">SĐT:</span> {selectedCall.driverPhone}</p>
                    <p><span className="font-medium">ID:</span> {selectedCall.driverId}</p>
                  </div>
                </div>

                <div className="p-4 bg-muted/50 rounded-lg">
                  <h3 className="font-semibold mb-2">Thông tin cuộc gọi</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Loại:</span> {selectedCall.callType === 'incoming' ? 'Gọi đến' : 'Gọi đi'}</p>
                    <p><span className="font-medium">Thời gian:</span> {new Date(selectedCall.timestamp).toLocaleString('vi-VN')}</p>
                    <p><span className="font-medium">Thời lượng:</span> {selectedCall.duration}</p>
                    <p><span className="font-medium">Trạng thái:</span> {selectedCall.status === 'completed' ? 'Hoàn thành' : 'Nhỡ'}</p>
                  </div>
                </div>

                <div className="p-4 bg-muted/50 rounded-lg">
                  <h3 className="font-semibold mb-2">Thông tin liên quan</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Đơn hàng:</span> {selectedCall.orderId}</p>
                    <p><span className="font-medium">Người nhận:</span> {selectedCall.recipientName}</p>
                    <p><span className="font-medium">SĐT nhận:</span> {selectedCall.recipientPhone}</p>
                    <p><span className="font-medium">Mục đích:</span> {selectedCall.purpose}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <PhoneCall className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p>Chọn một cuộc gọi để xem chi tiết</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CallHistory;